import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import { StarRating } from './StarRating';
import { colors } from '../../../theme';

export interface ReviewCardProps {
  /** Reviewer display name */
  reviewerName: string;
  /** Reviewer role / label e.g. "Parent", "Student" */
  reviewerRole?: string;
  /** Avatar image URL */
  avatarUrl?: string;
  /** Rating value 0–5 */
  rating: number;
  /** Review body text */
  text: string;
  /** Course or class title this review is for */
  courseTitle?: string;
  /** Date string e.g. "February 2026" */
  date?: string;
  /** Optional: highlight/featured style */
  featured?: boolean;
}

export const ReviewCard: React.FC<ReviewCardProps> = ({
  reviewerName,
  reviewerRole,
  avatarUrl,
  rating,
  text,
  courseTitle,
  date,
  featured = false,
}) => {
  const initials = reviewerName
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        backgroundColor: featured ? colors.primary.lightest : '#e8f4fd',
        border: featured ? `1.5px solid ${colors.primary.light}` : `1px solid #dde8f0`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': featured
          ? {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: 4,
              height: '100%',
              backgroundColor: colors.primary.main,
              borderRadius: '3px 0 0 3px',
            }
          : {},
      }}
    >
      {/* Rating row */}
      <Box mb={1.5}>
        <StarRating value={rating} size="small" />
      </Box>

      {/* Review text */}
      <Typography
        variant="body2"
        color="text.primary"
        mb={2}
        sx={{ lineHeight: 1.7, fontStyle: 'italic' }}
      >
        "{text}"
      </Typography>

      {/* Course tag */}
      {courseTitle && (
        <Typography
          variant="caption"
          sx={{
            display: 'inline-block',
            mb: 2,
            color: colors.primary.dark,
            backgroundColor: 'rgba(74,144,226,0.12)',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            fontWeight: 600,
          }}
        >
          {courseTitle}
        </Typography>
      )}

      {/* Reviewer row */}
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={1.25}>
          <Avatar
            src={avatarUrl}
            sx={{
              width: 36,
              height: 36,
              fontSize: '0.8125rem',
              fontWeight: 700,
              bgcolor: colors.primary.main,
              color: '#fff',
            }}
          >
            {!avatarUrl && initials}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={600} lineHeight={1.3}>
              {reviewerName}
            </Typography>
            {reviewerRole && (
              <Typography variant="caption" color="text.secondary">
                {reviewerRole}
              </Typography>
            )}
          </Box>
        </Box>

        {date && (
          <Typography variant="caption" color="text.disabled">
            {date}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default ReviewCard;
